<!-- =========================================
     TOYS STORE — 3-TIER NAVBAR
     Tier 1: Top utility bar (trust signals + account)
     Tier 2: Logo + Search + Cart actions
     Tier 3: Category nav strip
========================================= -->
<div class="site-header" [class.is-scrolled]="isScrolled">

<!-- ── TIER 1: TOP UTILITY BAR ── -->
<div class="top-bar d-none">
  <div class="top-bar__inner">

    <div class="top-bar__left">
      <span class="top-bar__trust">
        <i class='bx bx-badge-check'></i>
        Trusted Toy Store Since 2015
      </span>
      <span class="top-bar__divider" aria-hidden="true"></span>
      <span class="top-bar__trust">
        <i class='bx bx-shield-quarter'></i>
        100% Child-Safe Products
      </span>
      <span class="top-bar__divider" aria-hidden="true"></span>
      <span class="top-bar__trust">
        <i class='bx bx-package'></i>
        Free shipping on orders above ₹499
      </span>
    </div>

    <div class="top-bar__right">
      <a routerLink="/about" class="top-bar__link">About Us</a>
      <a routerLink="/contact" class="top-bar__link">Help</a>
      <span class="top-bar__divider" aria-hidden="true"></span>

      <!-- Guest state -->
      <ng-container *ngIf="!isLoggedIn()">
        <a routerLink="/profile-authentication" class="top-bar__link top-bar__link--accent">
          <i class='bx bx-user'></i> Sign In / Register
        </a>
      </ng-container>

      <!-- Logged-in state -->
      <ng-container *ngIf="isLoggedIn()">
        <div class="top-bar__user-mini">
          <a routerLink="/user-profile" class="top-bar__link">
            <i class='bx bx-user-circle'></i> Hi, {{ getUserName() }}
          </a>
          <button class="top-bar__link top-bar__link--muted" (click)="logout()">Logout</button>
        </div>
      </ng-container>
    </div>

  </div>
</div>

<!-- ── TIER 2: LOGO + SEARCH + ACTIONS ── -->
<div class="mid-bar">
  <div class="mid-bar__inner">

    <!-- Logo -->
    <a class="mid-bar__logo" routerLink="/" aria-label="Go to homepage">
      <img src="assets/img/slider/mainlogo.png" alt="Toy Store logo">
    </a>

    <!-- Search bar -->
    <div class="navbar-search-wrapper mid-bar__search" (clickOutside)="showSearchResults = false">

      <div class="premium-search input-group">

        <select class="category-select form-select" [(ngModel)]="selectedCategory" (change)="searchProducts()"
          aria-label="Filter by category">
          <option *ngFor="let category of projectCategories" [value]="category.id">
            {{ category.name }}
          </option>
        </select>

        <div class="search-divider" aria-hidden="true"></div>

        <input type="text" class="search-input form-control" [placeholder]="currentPlaceholder" [(ngModel)]="searchText"
          (focus)="showSearchResults = true" (input)="searchProducts()" aria-label="Search toys">

        <button class="search-submit-btn" (click)="searchProducts()" aria-label="Search">
          <i class='bx bx-search'></i>
        </button>

      </div>

      <!-- Floating Search Results -->
      <div class="search-results-dropdown" *ngIf="showSearchResults" role="listbox" aria-label="Search results">

        <ng-container *ngIf="filteredProducts?.length > 0">
          <div class="search-product" *ngFor="let product of filteredProducts" role="option">
            <img [src]="product.product_image" [alt]="product.product_name" loading="lazy">
            <div class="product-info">
              <h6 (click)="openProduct(product.product_id)" >{{ product.product_name }}</h6>
              <span class="category">{{ product.category }}</span>
              <div class="price">₹{{ product.mrp }}</div>
            </div>
            <i class='bx bx-chevron-right search-product__arrow'></i>
          </div>
        </ng-container>

        <div class="no-results" *ngIf="filteredProducts?.length === 0 || searchText?.trim()">
          <i class="bx bx-search-alt no-results__icon"></i>
          <h6 *ngIf="filteredProducts?.length === 0">No items found</h6>
          <p *ngIf="searchText?.trim()">
            No products match "<strong>{{ searchText }}</strong>"
          </p>
        </div>

      </div>

    </div>

    <!-- Action buttons -->
    <div class="mid-bar__actions">

      <!-- Wishlist -->
      <button class="action-btn" (click)="goToWishlist()" aria-label="Wishlist">
        <i class='bx bx-heart'></i>
        <!-- <span class="action-btn__label">Wishlist</span> -->
        <span class="action-badge" *ngIf="wishlistItemCount > 0" aria-label="{{ wishlistItemCount }} items">
          {{ wishlistItemCount }}
        </span>
      </button>

      <!-- Cart -->
      <button class="action-btn action-btn--cart" (click)="goToCart()" aria-label="Shopping cart">
        <i class='bx bx-cart'></i>
        <!-- <span class="action-btn__label">Cart</span> -->
        <span class="action-badge" *ngIf="cartItemCount > 0" aria-label="{{ cartItemCount }} items">
          {{ cartItemCount }}
        </span>
      </button>

      <a *ngIf="!isLoggedIn()"
        routerLink="/profile-authentication"
        class="action-btn action-btn--login"
        aria-label="Sign In / Register">
        <i class='bx bx-user'></i>
        <!-- <span class="action-btn__label">Sign In</span> -->
      </a>

      <!-- Profile (logged-in only) -->
      <div class="profile-menu-wrapper" *ngIf="isLoggedIn()" (clickOutside)="showProfileMenu = false">

        <button class="action-btn action-btn--profile" (click)="showProfileMenu = !showProfileMenu"
          aria-label="Account menu" [attr.aria-expanded]="showProfileMenu">

          <span class="profile-avatar-sm">
            <img *ngIf="getUserImage && getUserImage()" [src]="getUserImage()" alt="">
            <i *ngIf="!getUserImage || !getUserImage()" class='bx bx-user'></i>
          </span>

          <!-- <span class="action-btn__label">Account</span> -->
        </button>

        <!-- Dropdown panel -->
        <div class="profile-dropdown" *ngIf="showProfileMenu">

          <div class="profile-dropdown__header">
            <span class="profile-avatar-lg">
              <img *ngIf="getUserImage && getUserImage()" [src]="getUserImage()" alt="">
              <i *ngIf="!getUserImage || !getUserImage()" class='bx bx-user'></i>
            </span>
            <div class="profile-dropdown__who">
              <h6>{{ getUserName() }}</h6>
              <span class="profile-dropdown__role">Customer</span>
            </div>
          </div>

          <!-- <div class="profile-dropdown__status">
            <span class="status-dot"></span>
            Available
            <i class='bx bx-chevron-right status-arrow'></i>
          </div> -->

          <ul class="profile-dropdown__menu">

            <!-- <li (click)="goToProfileTab('profile')">
              <i class='bx bx-grid-alt'></i>
              Dashboard
            </li> -->

            <li (click)="goToProfileTab('profile')">
              <i class='bx bx-user-circle'></i>
              My Profile
            </li>

            <li (click)="goToProfileTab('addresses')">
              <i class='bx bx-map-pin'></i>
              Address Book
            </li>

            <li (click)="goToProfileTab('orders')">
              <i class='bx bx-shopping-bag'></i>
              My Orders
            </li>

            <li (click)="goToProfileTab('password')">
              <i class='bx bx-lock-alt'></i>
              Change Password
            </li>

          </ul>

          <div class="profile-dropdown__footer">
            <button class="profile-logout-btn" (click)="logout(); showProfileMenu = false">
              <i class='bx bx-log-out'></i>
              Sign Out
            </button>
          </div>

        </div>

      </div>

      <!-- Mobile hamburger (only visible < 992px) -->
      <button class="hamburger-btn" type="button" (click)="toggleClass()"
        [attr.aria-expanded]="classApplied" aria-label="Toggle navigation">
        <span class="hamburger-bar" [class.open]="classApplied"></span>
        <span class="hamburger-bar" [class.open]="classApplied"></span>
        <span class="hamburger-bar" [class.open]="classApplied"></span>
      </button>

    </div>

  </div>
</div>

<!-- ── TIER 3: CATEGORY NAV STRIP ── -->
<nav class="nav-strip" aria-label="Main navigation">
  <div class="nav-strip__inner">

    <!-- All Categories mega-button -->
    <div class="nav-strip__all-cats d-none">
      <i class='bx bx-grid-alt'></i>
      All Categories
      <i class='bx bx-chevron-down nav-strip__chevron'></i>
    </div>

    <div class="nav-strip__divider" aria-hidden="true"></div>

    <!-- Collapsible links (collapse on mobile) -->
    <div class="collapse navbar-collapse nav-strip__links" [class.show]="classApplied" id="mainNav">
      <ul class="nav-strip__list" role="list">

        <li class="nav-strip__item">
          <a routerLink="/" class="nav-strip__link" routerLinkActive="nav-strip__link--active"
            [routerLinkActiveOptions]="{exact: true}">
            Home
          </a>
        </li>

        <li class="nav-strip__item">
          <a routerLink="/menu" class="nav-strip__link" routerLinkActive="nav-strip__link--active"
            [routerLinkActiveOptions]="{exact: true}">
            Products
          </a>
        </li>

        <li class="nav-strip__item">
          <a routerLink="/about" class="nav-strip__link" routerLinkActive="nav-strip__link--active"
            [routerLinkActiveOptions]="{exact: true}">
            About Us
          </a>
        </li>

        <li class="nav-strip__item">
          <a routerLink="/contact" class="nav-strip__link" routerLinkActive="nav-strip__link--active"
            [routerLinkActiveOptions]="{exact: true}">
            Contact Us
          </a>
        </li>

      </ul>

      <!-- Promo badge (right side of nav strip) -->
      <div class="nav-strip__promo">
        <i class='bx bx-trending-up'></i>
        New arrivals every week
      </div>

    </div>

  </div>
</nav>

</div>


<!-- ── Checkout modal (unchanged) ── -->
<app-checkout-modal [show]="showCheckout" [orderData]="orderData" (close)="showCheckout=false">
</app-checkout-modal>

<!-- ── Enquiry modal (unchanged) ── -->
<div class="modal fade productsQuickView" id="enquireModal" tabindex="-1" role="dialog" aria-hidden="true">
  <div class="modal-dialog modal-dialog-centered" role="document">
    <div class="modal-content">
      <button type="button" class="close" data-bs-dismiss="modal" aria-label="Close">
        <span aria-hidden="true"><i class="bx bx-x"></i></span>
      </button>
      <div class="row align-items-center">
        <div class="col-lg-12 col-md-12">
          <div class="products-content">
            <h3>Enquire Now</h3>
            <p class="mb-4"><small class="text-muted">Fields marked with (<span style="color:red">*</span>) are
                required</small></p>
            <form [formGroup]="enquiryForm">
              <div class="row">
                <div class="col-lg-6 col-md-6">
                  <div class="form-group mb-3">
                    <label for="name">Name <span style="color:red">*</span></label>
                    <input formControlName="lead_name" class="form-control" id="name" type="text"
                      placeholder="Your name">
                    <div *ngIf="enquiryForm.get('lead_name')?.invalid && enquiryForm.get('lead_name')?.touched"
                      class="text-danger">Name is required.</div>
                  </div>
                </div>
                <div class="col-lg-6 col-md-6">
                  <div class="form-group mb-3">
                    <label for="phone">Phone <span style="color:red">*</span></label>
                    <input formControlName="contact_phone" class="form-control" id="phone" type="tel"
                      placeholder="Phone number" maxlength="10">
                    <div
                      *ngIf="enquiryForm.get('contact_phone')?.invalid && (enquiryForm.get('contact_phone')?.dirty || enquiryForm.get('contact_phone')?.touched)">
                      <div *ngIf="enquiryForm.get('contact_phone')?.errors?.required" class="text-danger">Phone is
                        required.</div>
                      <div *ngIf="enquiryForm.get('contact_phone')?.errors?.pattern" class="text-danger">Enter a valid
                        number.</div>
                    </div>
                  </div>
                </div>
                <div class="col-lg-6 col-md-6">
                  <div class="form-group mb-3">
                    <label for="email">Email</label>
                    <input formControlName="contact_email" class="form-control" id="email" type="email"
                      placeholder="Email address">
                  </div>
                </div>
                <div class="col-lg-6 col-md-6">
                  <div class="form-group mb-3">
                    <label>Customer Type <span style="color:red">*</span></label>
                    <select formControlName="customer_type" class="form-control">
                      <option value="" disabled>Select type</option>
                      <option *ngFor="let type of customerTypes" [value]="type.id">{{ type.value }}</option>
                    </select>
                    <div *ngIf="enquiryForm.get('customer_type')?.invalid && enquiryForm.get('customer_type')?.touched"
                      class="text-danger">Customer type is required.</div>
                  </div>
                </div>
                <div class="col-lg-6 col-md-6">
                  <div class="form-group mb-3">
                    <label>Location <span style="color:red">*</span></label>
                    <input formControlName="delivery_location" class="form-control" type="text"
                      placeholder="Your location">
                    <div
                      *ngIf="enquiryForm.get('delivery_location')?.invalid && enquiryForm.get('delivery_location')?.touched"
                      class="text-danger">Location is required.</div>
                  </div>
                </div>
                <div class="col-lg-12 col-md-12">
                  <div class="form-group mb-3">
                    <label>Message</label>
                    <textarea formControlName="scope_of_project" class="form-control" rows="3"
                      placeholder="Your message"></textarea>
                  </div>
                </div>
              </div>
            </form>
            <div class="products-add-to-cart">
              <button type="button" class="enquire-btn" (click)="addlead()" [disabled]="isLoading">
                <ng-container *ngIf="!isLoading; else loadingTpl">
                  <i class='bx bxs-paper-plane'></i> Enquire Now
                </ng-container>
                <ng-template #loadingTpl>
                  <i class="bx bx-loader-alt bx-spin"></i> Loading...
                </ng-template>
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</div>